import { each, isString, startCase } from 'lodash';

import { df, moment } from 'util/dateTime';
import { toI18n } from 'util/i18n';

export const formatEin = ein => ein.replace(/(\d{2})(\d{7})/, '$1-$2');

export const formatPhoneNumber = number => {
  if (!number || !number.toString().trim()) {
    return '';
  }

  // Strip all non-digit characters
  let digits = `${number}`.replace(/\D/g, '');

  // Handle leading country code (only supports US +1 for now)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  // In some cases we may receive >11 digits (e.g., extensions). Keep the last 10.
  if (digits.length > 11) {
    digits = digits.slice(-10);
  }

  // If we don't have exactly 10 digits, return the original (sanitised) string
  if (digits.length !== 10) {
    return number.toString();
  }

  const matches = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!matches) return number.toString();

  return `(${matches[1]}) ${matches[2]}-${matches[3]}`;
};

export const formatServerErrors = res => {
  const errors = {};

  each(res, (error, name) => {
    errors[name] = `${startCase(name)} ${error[0]}`;
  });

  return errors;
};

// Basic sanitization (existing function)
export const sanitizeString = value =>
  isString(value) ? value.replace(/<\/?script[^>]*>/g, '') : value;

// Comprehensive HTML sanitization for XSS prevention
export const sanitizeHtmlString = value => {
  if (!isString(value)) return value;

  let sanitized = value;

  // Step 1: Decode HTML entities FIRST to prevent bypassing with encoded content
  // Decode hex entities like &#x3C; &#x3E;
  sanitized = sanitized.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

  // Decode decimal entities like &#60; &#62;
  sanitized = sanitized.replace(/&#([0-9]+);/g, (match, dec) =>
    String.fromCharCode(parseInt(dec, 10))
  );

  // Decode named entities
  sanitized = sanitized.replace(/&lt;/g, '<');
  sanitized = sanitized.replace(/&gt;/g, '>');
  sanitized = sanitized.replace(/&quot;/g, '"');
  sanitized = sanitized.replace(/&#39;/g, "'");
  sanitized = sanitized.replace(/&amp;/g, '&');

  // Step 2: Remove script tags AND everything between them (including content)
  sanitized = sanitized.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  // Step 3: Remove other dangerous HTML tags (both opening and closing)
  const dangerousTags = [
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'textarea',
    'button',
    'select',
    'option',
    'img',
    'audio',
    'video',
    'source',
    'track',
    'canvas',
    'svg',
    'math',
    'link',
    'meta',
    'style',
  ];

  dangerousTags.forEach(tag => {
    const regex = new RegExp(`<\\/?${tag}[^>]*>`, 'gi');
    sanitized = sanitized.replace(regex, '');
  });

  // Step 4: Remove event handlers and javascript: links
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']?/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');

  // Step 5: Remove style attributes that could contain expressions
  sanitized = sanitized.replace(/\s*style\s*=\s*["'][^"']*["']?/gi, '');

  // Step 6: Remove HTML comments that could hide malicious code
  sanitized = sanitized.replace(/<!--[\s\S]*?-->/g, '');

  // Step 7: Remove dangerous text patterns (CSS blocks, function calls)
  sanitized = sanitized.replace(/alert\s*\([^)]*\)/gi, '');
  sanitized = sanitized.replace(/eval\s*\([^)]*\)/gi, '');
  sanitized = sanitized.replace(/\{[^}]*background[^}]*\}/gi, '');

  return sanitized;
};

export const capitalizeFirstLetter = text => startCase(text);

const parseDay = (value, format) => {
  if (typeof value !== 'string') return value;

  return moment(value, format);
};

export const centsToDollar = value =>
  Number((value ?? 0) / 100).toLocaleString();

export const formatShortDateRange = (
  startOfWeek,
  endOfWeek,
  format = df('parsable_date')
) => {
  const parsedStartOfWeek = parseDay(startOfWeek, format);
  const parsedEndOfWeek = parseDay(endOfWeek, format);

  if (parsedStartOfWeek.month() === parsedEndOfWeek.month()) {
    return `${parsedStartOfWeek.format('MMM D')} - ${parsedEndOfWeek.format(
      'D'
    )}`;
  }
  return `${parsedStartOfWeek.format('MMM D')} - ${parsedEndOfWeek.format(
    'MMM D'
  )}`;
};

export const formatLongDateRange = (
  startOfWeek,
  endOfWeek,
  format = df('parsable_date')
) => {
  const parsedStartOfWeek = parseDay(startOfWeek, format);
  const parsedEndOfWeek = parseDay(endOfWeek, format);

  const includeBothsMonths =
    parsedStartOfWeek.month() !== parsedEndOfWeek.month();
  const includeBothYears = parsedStartOfWeek.year() !== parsedEndOfWeek.year();

  const startYear = includeBothYears
    ? `, ${parsedStartOfWeek.format('YYYY')}`
    : '';
  const endYear = `, ${parsedEndOfWeek.format('YYYY')}`;

  return `${parsedStartOfWeek.format(
    'MMM D'
  )}${startYear} - ${parsedEndOfWeek.format(
    includeBothsMonths ? 'MMM D' : 'D'
  )}${endYear}`;
};

export const getProperties = message => {
  if (
    message.includes(
      toI18n('team.employee_profile.add_existing_user.resend_invite')
    )
  ) {
    return toI18n('team.employee_profile.error_msg.existing_error');
  } else if (
    message.includes(
      toI18n('activerecord.errors.models.user.attributes.email.taken')
    )
  ) {
    return toI18n('team.employee_profile.error_msg.email_taken');
  } else if (
    message.includes(
      toI18n('activerecord.errors.models.user.attributes.phone.taken')
    )
  ) {
    return toI18n('team.employee_profile.error_msg.phone_taken');
  }
  return message;
};
